import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { skSK } from '@clerk/localizations';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import { Layout } from './components/Layout';
import { CookieConsent } from './components/CookieConsent';
import { AppRoutes } from './components/AppRoutes';
import { GlobalErrorFallback } from './components/GlobalErrorFallback';
import { ThemeProvider } from './context/ThemeContext';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key. Please add VITE_CLERK_PUBLISHABLE_KEY to your .env.local file.');
}

const ClerkProviderWithRoutes = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      localization={skSK}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/login"
      signUpUrl="/signup"
      appearance={{
        layout: {
          socialButtonsPlacement: 'bottom',
          socialButtonsVariant: 'iconButton',
        },
        variables: {
          colorPrimary: '#1e3a8a',
          colorText: '#1e293b',
          colorTextSecondary: '#64748b',
          colorBackground: '#ffffff',
          colorInputBackground: '#f8fafc',
          colorInputText: '#1e293b',
          borderRadius: '12px',
        },
        elements: {
          card: 'shadow-xl border border-stone-100',
          formButtonPrimary: 'btn-primary-gradient',
          footerActionLink: 'text-primary hover:text-primary/90',
          headerTitle: '!text-stone-900',
          headerSubtitle: '!text-stone-500',
        }
      }}
    >
      {children}
    </ClerkProvider>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes cache by default
      retry: 1, // retry once on failure
      refetchOnWindowFocus: false, // don't refetch automatically when switching tabs
    },
  },
});

const App: React.FC = () => {
  React.useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch('/version.txt?t=' + Date.now(), { cache: 'no-store' });
        if (!res.ok) return;
        const liveVersion = (await res.text()).trim();
        const currentVersion = sessionStorage.getItem('app_live_version');
        if (currentVersion && liveVersion && currentVersion !== liveVersion) {
          sessionStorage.setItem('app_live_version', liveVersion);
          window.location.reload();
        } else if (liveVersion) {
          sessionStorage.setItem('app_live_version', liveVersion);
        }
      } catch {}
    };

    checkVersion();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
          <ThemeProvider defaultTheme="light" storageKey="espacero-theme-v3">
            <BrowserRouter>
              <ClerkProviderWithRoutes>
                <Layout>
                  <AppRoutes />
                  <CookieConsent />
                </Layout>
              </ClerkProviderWithRoutes>
            </BrowserRouter>
          </ThemeProvider>
        </ErrorBoundary>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

export default App;
